import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, MapPin, ArrowUpRight } from "lucide-react";
import { Input } from "../components/ui/input";
import Reveal from "../components/site/Reveal";
import { PageHero, CtaLink } from "../components/site/Common";
import { OrganicDot } from "../components/site/Logo";
import { Zones } from "../components/site/HomeSections";
import { CITIES, slugify } from "../mock";
import { AREAS, ALIASES, norm } from "../cities";
import CommunesMap from "../components/site/CommunesMap";

export default function Villes() {
  const [q, setQ] = useState("");
  const { hash } = useLocation();
  const highlight = decodeURIComponent(hash.replace("#", ""));
  useEffect(() => {
    if (!highlight) return;
    const el = document.getElementById(`city-${highlight}`);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
  }, [highlight]);

  const groups = useMemo(() => {
    const nq = norm(q);
    const list = CITIES.filter((c) => norm(c.name).includes(nq) || (ALIASES[c.name] || []).some((a) => norm(a).includes(nq)));
    const order = Object.values(AREAS);
    const g = list.reduce((acc, c) => { (acc[c.area] = acc[c.area] || []).push(c); return acc; }, {});
    return Object.fromEntries(order.filter((a) => g[a]).map((a) => [a, g[a]]));
  }, [q]);

  return (
    <div data-testid="villes-page">
      <PageHero kicker="Nos villes" title="partout où la mer" script="vous appelle" text={`Notre équipe vit sur le secteur et intervient dans ${CITIES.length} villes et villages de la Côte d'Émeraude et de la baie : de Saint-Cast-le-Guildo à Pontorson, en passant par Saint-Malo et Cancale.`}>
        <Reveal delay={220} className="mt-8 max-w-md mx-auto relative">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6d7268]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une ville…" className="h-14 rounded-full pl-12 text-base bg-white" data-testid="cities-search" />
        </Reveal>
      </PageHero>
      <CommunesMap />
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-12">
          {Object.keys(groups).length === 0 && (
            <div className="text-center rounded-[28px] bg-[#f3ede1] p-10" data-testid="cities-empty">
              <p className="font-bold text-[#13261c] text-lg">Votre ville n'est pas dans la liste ?</p>
              <p className="text-[#5a6158] mt-1">Contactez-nous, nous étendons régulièrement notre secteur.</p>
              <div className="mt-5"><CtaLink to="/contact">Nous contacter</CtaLink></div>
            </div>
          )}
          {Object.entries(groups).map(([area, list]) => (
            <div key={area}>
              <h2 className="font-logo font-black tracking-[-0.025em] text-3xl lg:text-4xl text-[#13261c]">{area}<OrganicDot className="inline-block w-2.5 h-2.5 ml-1 align-baseline" /> <span className="text-base font-sans font-bold text-[#c4703f] align-middle ml-2">{list.length} communes</span></h2>
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {list.map((c, i) => (
                  <Reveal key={c.name} delay={(i % 4) * 60}>
                    <Link id={`city-${c.name}`} to={`/villes/${slugify(c.name)}`} data-testid={`city-card-${slugify(c.name)}`}
                      className={`group flex items-center justify-between rounded-2xl p-5 ring-1 transition-colors ${highlight === c.name ? "bg-[#13261c] text-white ring-[#13261c]" : "bg-white ring-[#e6dcc8] hover:bg-[#13261c] hover:text-white text-[#13261c]"}`}>
                      <span className="flex items-center gap-3"><MapPin className="w-5 h-5 text-[#c4703f] group-hover:text-[#d9895a]" /><span><span className="block font-bold">{c.name}</span>{ALIASES[c.name] && <span className="block text-xs opacity-70" data-testid={`city-aliases-${slugify(c.name)}`}>{ALIASES[c.name].join(" · ")}</span>}</span></span>
                      <ArrowUpRight className="w-5 h-5 opacity-40 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <Zones />
    </div>
  );
}
